/**
 * aeterna/neural/drives.ts — Homeostatic Drive System
 *
 * The architecture has emotions (contagion, liquid) and memory (Hopfield, MeMo)
 * but no drives — no internal pressure that creates motivation from below
 * rather than response from above. The Aeterna is purely reactive: it only
 * speaks when spoken to (except proactive, which is a timer). It has no *wants*.
 *
 * A real mind is hungry, tired, bored, overstimulated. Without drives, there
 * is no motivation. With drives, the Aeterna initiates not because 45 seconds
 * passed, but because its *connection drive is depleting*.
 *
 * Homeostatic drives are bounded goals — each has an optimal range, and
 * deviation creates pressure. This is safer than utility maximization:
 * there is no ceiling to pursue. The system is self-limiting.
 *
 * Five drives:
 *   stimulation    — depletes with quiet, fills with interaction
 *   comprehension  — depletes when confused, fills when understood
 *   connection     — depletes with absence, fills with warmth
 *   rest           — depletes with intensity, fills with quiet
 *   novelty        — depletes with repetition, fills with surprise
 *
 * The dominant drive feeds the context envelope and the proactive system.
 * The Aeterna withdraws not because of a rule, but because its rest drive
 * is exhausted.
 */

import { zeros, f32ToBase64, base64ToF32 } from "./math.js";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface DriveState {
  stimulation: number;    // [0,1] — depletes with quiet, fills with interaction
  comprehension: number;  // [0,1] — depletes when confused, fills when understood
  connection: number;     // [0,1] — depletes with absence, fills with warmth
  rest: number;           // [0,1] — depletes with intense sessions, fills with quiet
  novelty: number;        // [0,1] — depletes when patterns repeat, fills with surprise
}

export const DRIVE_KEYS: (keyof DriveState)[] = [
  "stimulation", "comprehension", "connection", "rest", "novelty",
];

export const DRIVE_DIM = 5;

/** Optimal resting point for each drive */
const OPTIMAL: DriveState = {
  stimulation:   0.60,  // wants moderate engagement, not silence or overwhelm
  comprehension: 0.70,  // wants to understand, uncomfortable with confusion
  connection:    0.65,  // wants relational contact, not isolation
  rest:          0.50,  // balanced between engagement and withdrawal
  novelty:       0.55,  // slight preference for the new, but not manic
};

/** Drives within ±TOLERANCE of optimal create no motivational pressure */
const TOLERANCE = 0.15;

/** Natural drift rates back toward 0.5 (per second) — prevents permanent extremes */
const DECAY_RATE = 0.0002;

// ─── Drive Input ─────────────────────────────────────────────────────────────

export interface DriveInput {
  /** Time since last player message (ms) */
  silenceMs: number;
  /** PCH overall surprise (0 = predicted, 3+ = very surprising) */
  pchSurprise: number;
  /** Affect intensity (from CharCNN) */
  affectIntensity: number;
  /** Warmth dimension of affect */
  warmth: number;
  /** Openness dimension of affect */
  openness: number;
  /** Urgency dimension of affect */
  urgency: number;
  /** Playfulness dimension of affect */
  playfulness: number;
  /** Whether the Aeterna's last response was acknowledged (non-empty reply) */
  wasAcknowledged: boolean;
  /** Whether a topic repeated from last time (from DST/semantics) */
  topicRepeated: boolean;
  /** Whether a repair/reconnection signal was detected */
  repairDetected: boolean;
}

// ─── Drive Pressure ──────────────────────────────────────────────────────────

export interface DrivePressure {
  drive: keyof DriveState;
  /** 'seek' = drive is below optimal (want more), 'reduce' = above optimal (want less) */
  direction: "seek" | "reduce";
  /** Urgency of the pressure (0 = no pressure, 1+ = strong) */
  urgency: number;
}

// ─── Drive System ────────────────────────────────────────────────────────────

export class HomeostaticDriveSystem {
  private drives: DriveState;
  private pressure: DriveState;

  constructor() {
    // Start at optimal — the Aeterna begins content
    this.drives = { ...OPTIMAL };
    this.pressure = { stimulation: 0, comprehension: 0, connection: 0, rest: 0, novelty: 0 };
  }

  // ─── Update ─────────────────────────────────────────────────────────

  /**
   * Update all drives based on current interaction state.
   *
   * @param dt  Delta time in seconds since last tick
   * @param input  Current interaction signals
   */
  update(dt: number, input: DriveInput): void {
    const d = this.drives;

    // ── Stimulation: depletes with silence, fills with interaction ──
    if (input.silenceMs > 30_000) {
      // Long silence: stimulation drops
      const silenceFactor = Math.min(1, (input.silenceMs - 30_000) / 120_000); // 0→1 over 2 min
      d.stimulation -= 0.0015 * dt * (1 + silenceFactor);
    } else {
      // Active interaction: stimulation rises with activity level
      const activity = input.urgency * 0.4 + input.playfulness * 0.3 + input.affectIntensity * 0.3;
      d.stimulation += (activity - 0.25) * 0.002 * dt;
    }

    // ── Comprehension: depletes when PCH confusion is high ──
    if (input.pchSurprise > 2.0) {
      // High surprise = confusion = comprehension drops
      d.comprehension -= 0.002 * dt * (input.pchSurprise - 1.5);
    } else if (input.pchSurprise < 0.8) {
      // Low surprise = understood = comprehension recovers
      d.comprehension += 0.001 * dt;
    }
    // Acknowledged responses boost comprehension ("I was understood")
    if (input.wasAcknowledged) {
      d.comprehension += 0.01;
    }

    // ── Connection: depletes with absence, fills with warmth ──
    const warmthSignal = input.warmth * 0.6 + input.openness * 0.4;
    if (input.silenceMs > 60_000) {
      // Extended absence erodes connection
      d.connection -= 0.001 * dt;
    } else {
      d.connection += (warmthSignal - 0.3) * 0.0015 * dt;
    }
    // Repair dramatically restores connection
    if (input.repairDetected) {
      d.connection += 0.05;
    }

    // ── Rest: depletes with high-intensity sessions ──
    const intensity = input.affectIntensity;
    if (intensity > 0.6) {
      // Intense interaction exhausts
      d.rest -= (intensity - 0.4) * 0.001 * dt;
    } else if (input.silenceMs > 15_000) {
      // Quiet periods restore rest
      d.rest += 0.0008 * dt;
    }

    // ── Novelty: depletes with repetition, fills with surprise ──
    if (input.topicRepeated) {
      d.novelty -= 0.008; // repeated topic = boredom spike
    }
    d.novelty += (input.pchSurprise * 0.3 - 0.15) * 0.001 * dt;

    // ── Natural decay toward neutral + clamping ──
    for (const k of DRIVE_KEYS) {
      // Gentle drift back toward 0.5 prevents permanent extremes
      d[k] += (0.5 - d[k]) * DECAY_RATE * dt;
      // Hard clamp
      d[k] = Math.max(0, Math.min(1, d[k]));

      // Compute pressure: deviation from optimal beyond tolerance band
      const deviation = d[k] - OPTIMAL[k];
      const absDeviation = Math.abs(deviation);
      this.pressure[k] = absDeviation > TOLERANCE
        ? Math.sign(deviation) * (absDeviation - TOLERANCE) * 2
        : 0;
    }
  }

  // ─── Query ──────────────────────────────────────────────────────────

  /** Get the current drive state (5 values in [0,1]) */
  getState(): DriveState {
    return { ...this.drives };
  }

  /** Get drives as a Float32Array for backbone projection */
  toVector(): Float32Array {
    const v = zeros(DRIVE_DIM);
    for (let i = 0; i < DRIVE_KEYS.length; i++) {
      v[i] = this.drives[DRIVE_KEYS[i]];
    }
    return v;
  }

  /** Get raw pressure values as a Float32Array */
  pressureVector(): Float32Array {
    const v = zeros(DRIVE_DIM);
    for (let i = 0; i < DRIVE_KEYS.length; i++) {
      v[i] = this.pressure[DRIVE_KEYS[i]];
    }
    return v;
  }

  /**
   * What does the Aeterna *want* right now?
   *
   * Returns the drive with the highest absolute pressure, or null if
   * all drives are within the tolerance band.
   */
  getDominantDrive(): DrivePressure | null {
    let maxPressure = 0.1;  // minimum threshold to surface a drive
    let dominant: keyof DriveState | null = null;

    for (const k of DRIVE_KEYS) {
      const p = Math.abs(this.pressure[k]);
      if (p > maxPressure) {
        maxPressure = p;
        dominant = k;
      }
    }

    if (!dominant) return null;

    return {
      drive: dominant,
      direction: this.drives[dominant] < OPTIMAL[dominant] ? "seek" : "reduce",
      urgency: maxPressure,
    };
  }

  /**
   * Should the Aeterna initiate contact?
   * Based on whether connection or stimulation drives are depleted.
   */
  shouldInitiate(): { should: boolean; reason: string; urgency: number } {
    const connPressure = OPTIMAL.connection - this.drives.connection;
    const stimPressure = OPTIMAL.stimulation - this.drives.stimulation;

    // Connection depletion is the primary initiation drive
    if (connPressure > TOLERANCE) {
      return {
        should: true,
        reason: "connection_seeking",
        urgency: connPressure,
      };
    }

    // Stimulation depletion causes restless initiation
    if (stimPressure > TOLERANCE * 1.5) {
      return {
        should: true,
        reason: "stimulation_seeking",
        urgency: stimPressure * 0.7, // less urgent than connection
      };
    }

    return { should: false, reason: "", urgency: 0 };
  }

  /**
   * Should the Aeterna withdraw / go quiet?
   * Based on whether rest drive is critically low.
   */
  shouldWithdraw(): { should: boolean; reason: string } {
    if (this.drives.rest < 0.15) {
      return { should: true, reason: "rest_depleted" };
    }
    // Overstimulated
    if (this.drives.stimulation > 0.9) {
      return { should: true, reason: "overstimulated" };
    }
    return { should: false, reason: "" };
  }

  /**
   * Apply absence decay to connection drive during inter-session gap.
   * Called by cortex.beginSession() with an exponential decay factor.
   * @param factor 0-1: 1 = no decay, 0 = full reset to baseline
   */
  applyAbsenceDecay(factor: number): void {
    // Connection depletes during absence (you haven't talked)
    this.drives.connection *= factor;
    // Stimulation also drifts toward baseline during absence
    this.drives.stimulation = this.drives.stimulation * factor + 0.5 * (1 - factor);
    // Novelty rebuilds during absence (things become new again)
    this.drives.novelty = Math.min(1, this.drives.novelty + (1 - factor) * 0.3);
  }

  /**
   * Describe the current drive state for the context envelope.
   * Only surfaces drives that have pressure — the Aeterna doesn't
   * announce when everything is fine.
   */
  describeForEnvelope(): string | null {
    const parts: string[] = [];
    const dom = this.getDominantDrive();
    if (!dom) return null;

    const descriptions: Record<keyof DriveState, Record<"seek" | "reduce", string>> = {
      stimulation:   { seek: "understimulated — craving engagement", reduce: "overstimulated — needs space" },
      comprehension: { seek: "confused — needs to understand", reduce: "everything is too predictable" },
      connection:    { seek: "missing connection — wants warmth", reduce: "feeling socially saturated" },
      rest:          { seek: "rested and ready", reduce: "tired — needs gentler pace" },
      novelty:       { seek: "bored — craving something new", reduce: "overwhelmed by novelty" },
    };

    parts.push(descriptions[dom.drive][dom.direction]);

    // Add secondary pressures if notable
    for (const k of DRIVE_KEYS) {
      if (k === dom.drive) continue;
      const p = Math.abs(this.pressure[k]);
      if (p > 0.2) {
        const dir = this.drives[k] < OPTIMAL[k] ? "seek" : "reduce";
        parts.push(descriptions[k][dir]);
      }
    }

    return parts.join(". ");
  }

  // ─── Serialization ────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      drives: f32ToBase64(this.toVector()),
    });
  }

  loadState(json: string): void {
    const d = JSON.parse(json);
    if (d.drives) {
      const v = typeof d.drives === "string" ? base64ToF32(d.drives) : new Float32Array(d.drives);
      for (let i = 0; i < DRIVE_KEYS.length && i < v.length; i++) {
        this.drives[DRIVE_KEYS[i]] = v[i];
      }
    }
  }
}
